// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { ClipboardCheck, Construction, Inbox } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePendingActionsQuery } from "@/api/hooks/use-pending-actions";
import { PageHeader } from "@/components/layout/page-header";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ActionCard } from "@/features/approvals/action-card";
import { useAuth } from "@/lib/auth";

type Tab = "pending" | "resolved";

export function ApprovalsPage() {
  const { t } = useTranslation("back-office");
  const { user } = useAuth();
  const { data } = usePendingActionsQuery();
  const [tab, setTab] = useState<Tab>("pending");
  const isAdmin = user?.groups.includes("admin") ?? false;

  const pending = useMemo(() => data?.actions.filter((a) => a.status === "pending") ?? [], [data]);
  const resolved = useMemo(() => data?.actions.filter((a) => a.status !== "pending") ?? [], [data]);
  const items = tab === "pending" ? pending : resolved;

  return (
    <div>
      <PageHeader
        title={t("approvals.title")}
        icon={ClipboardCheck}
        actions={
          <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
            <TabsList>
              <TabsTrigger value="pending">
                {t("approvals.tabs.pending")}
                {pending.length > 0 && (
                  <span className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-xs font-medium text-white">
                    {pending.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="resolved">{t("approvals.tabs.resolved")}</TabsTrigger>
            </TabsList>
          </Tabs>
        }
      />
      <div className="flex items-center gap-2 mx-6 mt-6 px-4 py-2 rounded-md border border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-400 text-xs">
        <Construction className="h-3.5 w-3.5 shrink-0" />
        {t("approvals.wipBanner")}
      </div>
      <div className="flex flex-col gap-3 p-6 pt-3">
        {items.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-sm text-muted-foreground">
            <Inbox className="mb-2 h-8 w-8 opacity-30" />
            {tab === "pending" ? t("approvals.noActions") : t("approvals.noResolvedActions")}
          </div>
        )}
        {items.map((action) => (
          <ActionCard key={action.id} action={action} isAdmin={isAdmin} />
        ))}
      </div>
    </div>
  );
}
